import React from "react";
import { Link } from "react-router-dom";

export default function LandingPage() {
  const heroBg =
    "linear-gradient(140deg, rgba(7, 12, 25, 0.93), rgba(8, 47, 73, 0.9), rgba(15, 23, 42, 0.9)), url('/backroundlanding.png')";
  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col">
      <header className="border-b border-slate-800">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-2">
            <span className="text-xl md:text-2xl font-bold tracking-tight text-slate-50">
              ClaimSupplement<span className="text-sky-300">.ai</span>
            </span>
          </div>

          <nav className="hidden gap-6 text-sm text-slate-300 md:flex">
            <a href="#how-it-works" className="hover:text-sky-300 transition-colors">
              How it works
            </a>
            <a href="#features" className="hover:text-sky-300 transition-colors">
              Features
            </a>
            <a href="#pricing" className="hover:text-sky-300 transition-colors">
              Pricing
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm text-slate-300 hover:text-sky-300 transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/login"
              className="pb-1 text-sm font-semibold text-sky-300 underline decoration-sky-300 decoration-2 underline-offset-4 transition-colors hover:text-sky-200 hover:decoration-sky-200"
            >
              Get started
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <section
          className="border-b border-slate-800"
          style={{
            backgroundImage: heroBg,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 md:grid-cols-2 md:py-24">
            <div className="space-y-6">
              <p className="inline-flex text-xs font-semibold uppercase tracking-[0.18em] text-sky-300/80">
                Built for public adjusters & contractors
              </p>
              <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-50">
                Turn carrier estimates into{" "}
                <span className="text-sky-300">supplements in minutes</span>,
                not days.
              </h1>
              <p className="text-base text-slate-300 md:text-lg">
                Upload both estimates, auto-align rooms and line items, annotate PDFs
                and photos, and generate AI-backed variance notes your carriers
                can&apos;t ignore.
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Link
                  to="/login"
                  className="rounded-lg bg-sky-400 px-6 py-2.5 text-sm font-semibold text-slate-900 shadow-lg shadow-sky-400/30 hover:bg-sky-300 transition-colors"
                >
                  Start a free report
                </Link>
                <Link
                  to="/login"
                  className="text-sm font-medium text-slate-200 hover:text-sky-300 transition-colors"
                >
                  Sign in to your workspace →
                </Link>
              </div>

              <div className="text-xs text-slate-400">Account access is free. Pay only when you export reports.</div>

              <ul className="grid gap-2 pt-4 text-xs text-slate-400 sm:grid-cols-2">
                <li className="list-disc list-inside">Compare carrier vs. PA estimates line-by-line</li>
                <li className="list-disc list-inside">Annotate PDFs and photos with scope callouts</li>
                <li className="list-disc list-inside">Attach photos directly to rooms and line items</li>
                <li className="list-disc list-inside">
                  Full contents inventory from photos only (AI)
                </li>
              </ul>
            </div>

            <div className="flex items-center justify-center">
              <div className="w-full max-w-md rounded-3xl border border-slate-800 bg-slate-900/60 p-4 shadow-xl shadow-slate-900/80 backdrop-blur">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                    <span className="h-2.5 w-2.5 rounded-full bg-sky-400/80" />
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
                  </div>
                  <span className="text-xs text-slate-300">Workflow snapshot</span>
                </div>

                <div className="space-y-2 rounded-2xl bg-slate-950/40 p-3 border border-slate-800/70">
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>Room</span>
                    <span>Variance</span>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex items-center justify-between rounded-xl bg-slate-900/80 px-3 py-2">
                      <div>
                        <div className="font-medium text-slate-100">Kitchen</div>
                        <div className="text-slate-400">Tile floor, baseboards</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sky-300 font-semibold">+ $4,320</div>
                        <div className="text-[10px] text-slate-400">6 missing line items</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between rounded-xl bg-slate-900/60 px-3 py-2">
                      <div>
                        <div className="font-medium text-slate-100">Master Bath</div>
                        <div className="text-slate-400">Tile & cabinetry</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sky-300 font-semibold">+ $2,110</div>
                        <div className="text-[10px] text-slate-400">3 missing line items</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between rounded-xl bg-slate-900/40 px-3 py-2">
                      <div>
                        <div className="font-medium text-slate-100">Living Room</div>
                        <div className="text-slate-400">Flooring & paint</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sky-300 font-semibold">+ $8,950</div>
                        <div className="text-[10px] text-slate-400">O&P + scope</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between border-t border-slate-800 pt-3 text-xs">
                    <span className="text-slate-400">Total variance (RCV)</span>
                    <span className="text-lg font-semibold text-emerald-300">
                      + $21,380
                    </span>
                  </div>
                  <div className="grid gap-2 pt-2 text-[11px] text-slate-400 sm:grid-cols-2">
                    <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-2 py-1">
                      Photo Booth: 18 photos linked
                    </div>
                    <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-2 py-1">
                      PDF annotations: 12 callouts
                    </div>
                    <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-2 py-1">
                      AI scope flags: 9 items
                    </div>
                    <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-2 py-1">
                      Packet ready: 1 click export
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-800 bg-slate-950">
          <div className="mx-auto max-w-6xl px-4 py-12">
            <style>{`
              :root {
                --takeaway-height: 48px;
              }
              .takeaway-viewport {
                height: var(--takeaway-height);
              }
              .takeaway-track {
                animation: takeaway-cycle 24s infinite;
              }
              .takeaway-item {
                height: var(--takeaway-height);
              }
              @keyframes takeaway-cycle {
                0%, 20% { transform: translateY(0); }
                25%, 45% { transform: translateY(calc(var(--takeaway-height) * -1)); }
                50%, 70% { transform: translateY(calc(var(--takeaway-height) * -2)); }
                75%, 95% { transform: translateY(calc(var(--takeaway-height) * -3)); }
                100% { transform: translateY(calc(var(--takeaway-height) * -4)); }
              }
            `}</style>
            <div className="grid gap-6 md:grid-cols-[1.1fr_0.9fr] md:items-center">
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-300/80">
                  Key takeaways
                </p>
                <h2 className="text-3xl font-semibold text-slate-50 md:text-4xl">
                  Carrier-facing packets, without the busywork.
                </h2>
                <p className="text-sm text-slate-300">
                  Each workflow step is built to surface what carriers need most: scope deltas,
                  clear evidence, and fast approval signals.
                </p>
              </div>
              <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-5 shadow-xl shadow-slate-900/70">
                <div className="takeaway-viewport overflow-hidden">
                  <div className="takeaway-track motion-reduce:animate-none">
                    <div className="takeaway-item flex items-center text-lg font-semibold text-slate-100 md:text-xl">
                      Full contents inventory from photos only.
                    </div>
                    <div className="takeaway-item flex items-center text-lg font-semibold text-slate-100 md:text-xl">
                      Line-by-line scope deltas with evidence.
                    </div>
                    <div className="takeaway-item flex items-center text-lg font-semibold text-slate-100 md:text-xl">
                      Priority packets carriers actually open.
                    </div>
                    <div className="takeaway-item flex items-center text-lg font-semibold text-slate-100 md:text-xl">
                      AI variance notes ready to paste.
                    </div>
                    <div className="takeaway-item flex items-center text-lg font-semibold text-slate-100 md:text-xl">
                      Full contents inventory from photos only.
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-xs text-slate-400">
                  Built to keep adjusters focused on approvals, not formatting.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="border-b border-slate-800 bg-slate-950">
          <div className="mx-auto max-w-6xl px-4 py-12 space-y-6">
            <h2 className="text-xl font-semibold text-slate-50">How it works</h2>
            <ol className="grid gap-4 text-sm text-slate-300 md:grid-cols-3">
              <li className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="mb-2 text-xs font-semibold text-sky-300">Step 1</div>
                Upload carrier and PA estimates as PDFs (plus any photos).
              </li>
              <li className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="mb-2 text-xs font-semibold text-sky-300">Step 2</div>
                Auto-map rooms and line items, then annotate PDFs and photos.
              </li>
              <li className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="mb-2 text-xs font-semibold text-sky-300">Step 3</div>
                Let AI draft variance notes, then export a clean packet for submission.
              </li>
            </ol>
          </div>
        </section>

        <section id="features" className="border-b border-slate-800 bg-slate-950">
          <div className="mx-auto max-w-6xl px-4 py-12">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-50">
                  Everything inside ClaimSupplement<span className="text-sky-300">.ai</span>
                </h2>
                <p className="mt-1 text-sm text-slate-300">
                  Evidence-first workflows, AI integration, and packet-ready exports built for real claims.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 text-xs text-slate-400">
                <span className="rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1">
                  PDF annotations
                </span>
                <span className="rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1">
                  Photo Booth
                </span>
                <span className="rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1">
                  AI variance notes
                </span>
              </div>
            </div>

            <div className="mt-6 grid gap-4 text-sm text-slate-300 md:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900/80 via-slate-900/60 to-slate-950/80 p-5 md:col-span-2">
                <div className="mb-2 text-xs font-semibold text-sky-300">Photo Booth evidence</div>
                <p className="text-slate-200">
                  Add building and contents photos by room, attach shots to specific line items,
                  and write captions so scope evidence stays tied to the right variance.
                </p>
                <div className="mt-3 grid gap-2 text-[11px] text-slate-400 sm:grid-cols-2">
                  <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
                    Room overviews + line-item shots
                  </div>
                  <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
                    Photo captions for carrier review
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                <div className="mb-2 text-xs font-semibold text-sky-300">PDF + photo annotation</div>
                Mark up estimate PDFs and photos directly in the app. Drop callouts, shapes, and notes that
                export cleanly into the packet.
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                <div className="mb-2 text-xs font-semibold text-sky-300">Contents Inventory (AI)</div>
                Build a full contents inventory from photos alone. Auto-capture items, quantities, and
                replacement value estimates without manual data entry.
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                <div className="mb-2 text-xs font-semibold text-sky-300">AI variance notes</div>
                Generate scope-gap notes and trade recap narratives automatically. AI highlights high-confidence
                drivers so you can review and ship faster.
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                <div className="mb-2 text-xs font-semibold text-sky-300">Room-aware matching</div>
                Auto-detect rooms, map sections, and focus on meaningful deltas with built-in thresholds for
                quantity, unit price, and total variance.
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                <div className="mb-2 text-xs font-semibold text-sky-300">Packet builder</div>
                Export annotated PDFs, evidence photos, and variance tables into a clean packet that is ready
                for carrier submission.
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                <div className="mb-2 text-xs font-semibold text-sky-300">QA + progress tracking</div>
                Save progress, flag AI-highlighted items, and keep your review workflow clean from intake to
                final packet.
              </div>
            </div>
          </div>
        </section>

        <section id="pricing" className="bg-slate-950">
          <div className="mx-auto max-w-6xl px-4 py-12">
            <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 md:flex md:items-center md:justify-between">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-slate-50">
                  Simple pricing made for claim pros
                </h3>
                <p className="text-sm text-slate-300">
                  Pay-per-report or go unlimited for your whole team.
                </p>
              </div>
              <div className="mt-4 flex flex-col items-start gap-2 text-sm text-slate-200 md:mt-0 md:items-end">
                <div>
                  <span className="font-semibold text-sky-300">$25</span>{" "}
                  per report •{" "}
                  <span className="font-semibold text-sky-300">$100</span>{" "}
                  / month unlimited
                </div>
                <Link
                  to="/login"
                  className="mt-1 rounded-lg bg-sky-400 px-4 py-2 text-xs font-semibold text-slate-900 shadow hover:bg-sky-300 transition-colors"
                >
                  Sign in to start billing →
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 text-xs text-slate-500">
          <span>
            © {new Date().getFullYear()} ClaimSupplement<span className="text-sky-300">.ai</span>
          </span>
          <div className="flex gap-4">
            <button className="hover:text-sky-300 transition-colors">Terms</button>
            <button className="hover:text-sky-300 transition-colors">Privacy</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
